import React, { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import * as Progress from '@radix-ui/react-progress';
import { CheckCircleIcon } from '@heroicons/react/solid';
import Layout from '../../../components/Layout';
import ModuleConfiguration from './components/ModuleConfiguration';
import PatientSetupFields from './components/PatientSetupFields';

export default function ProviderOnboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const steps = [
    {
      number: 1,
      title: "Module Configuration",
      description: "Select and configure the modules that make up your clinical workflow",
      isComplete: currentStep > 1
    },
    {
      number: 2,
      title: "Patient Setup Fields",
      description: "Configure the required fields and options for new patient records",
      isComplete: currentStep > 2
    },
    {
      number: 3,
      title: "Assessment Questionnaires",
      description: "Choose additional standardised questionnaires for patient assessments",
      isComplete: currentStep > 3
    }
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Configure Your Platform</h1>
          <p className="mt-2 text-sm text-gray-600">
            Let's set up the platform to match your clinical workflow and requirements.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress.Root 
            className="relative overflow-hidden bg-gray-200 rounded-full w-full h-2"
            value={(currentStep / totalSteps) * 100}
          >
            <Progress.Indicator 
              className="bg-violet-600 w-full h-full transition-transform duration-400 ease-in-out"
              style={{ transform: `translateX(-${100 - (currentStep / totalSteps) * 100}%)` }}
            />
          </Progress.Root>
        </div>

        {/* Steps Overview */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {steps.map((step) => (
            <div 
              key={step.number}
              className={`relative rounded-lg border p-6 ${
                step.number === currentStep 
                  ? 'border-violet-600 bg-violet-50' 
                  : step.isComplete 
                    ? 'border-green-600 bg-green-50' 
                    : 'border-gray-200'
              }`}
            >
              {step.isComplete && (
                <CheckCircleIcon className="absolute top-4 right-4 h-6 w-6 text-green-600" />
              )}
              <span className={`text-sm font-medium ${
                step.number === currentStep ? 'text-violet-600' : 'text-gray-500'
              }`}>
                Part {step.number}
              </span>
              <h3 className="mt-2 text-lg font-medium text-gray-900">{step.title}</h3>
              <p className="mt-2 text-sm text-gray-600">{step.description}</p>
            </div>
          ))}
        </div>

        {/* Main Content Area */}
        <Tabs.Root 
          value={`step-${currentStep}`} 
          onValueChange={(value) => setCurrentStep(parseInt(value.split('-')[1]))}
        >
          <Tabs.Content value="step-1">
            {/* Module Configuration Content */}
            <div className="">              
              <ModuleConfiguration />
            </div>
          </Tabs.Content>

          <Tabs.Content value="step-2">
            {/* Patient Setup Fields Content */}
            <div className="">              
            <PatientSetupFields />
            </div>
          </Tabs.Content>

          <Tabs.Content value="step-3">
            {/* Assessment Questionnaires Content */}
            <div className="bg-white rounded-lg shadow p-6">
              {/* Assessment questionnaires content will go here */}
            </div>
          </Tabs.Content>
        </Tabs.Root>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentStep(Math.min(totalSteps, currentStep + 1))}
            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          >
            {currentStep === totalSteps ? 'Complete Setup' : 'Next Step'}
          </button>
        </div>
      </div>
    </Layout>
  );
}